import { Dialog, Button, Box, Text, Stack } from '@machobear/design-system'
import { useTranslation } from 'react-i18next'
import { format, parseISO } from 'date-fns'
import useGetCreditReports from '@/hooks/use-get-credit-report'
import { InfoItem } from './info-item'
type Props = {
  visible: boolean
  onClose: () => void
  submitConfirmButton: () => void
  applicationId: string
}
const getFullAddress = (
  data:
    | {
        unit_number: string
        street_number: string
        street_name: string
        city: string
        province: string
        zip: string
      }
    | undefined
) => {
  if (!data) {
    return undefined
  }
  let returnString = ''
  const unitText = `${data.unit_number} - `
  if (data.unit_number) {
    returnString += unitText
  }
  return (returnString += `${data.street_number} ${data.street_name} ${data.city}, ${data.province} ${data.zip}`)
}

const TrustScoreAgreementModal = ({
  visible,
  onClose,
  submitConfirmButton,
  applicationId
}: Props) => {
  const { t } = useTranslation()
  const { data, isLoading } = useGetCreditReports(applicationId)
  const fullAddress = getFullAddress(data)

  const onConfirmHandler = () => {
    submitConfirmButton()
    onClose()
  }
  const formatedDate = data?.dob && format(parseISO(data.dob), 'MM-dd-yyyy')
  return (
    <Dialog
      id="trust-score-agreement-modal"
      open={visible}
      onClose={onClose}
      title={t('l.credit_check_authorization', 'Credit Check Authorization')}
      width="small"
    >
      <Box paddingTop="small">
        <Stack space="gutter">
          <Box>
            <Text size="standard" weight="regular" align="left">
              {t(
                'l.trust_score_auth.text',
                "By unlocking this applicant's Trust Score report, you authorize liv.rent to submit an Equifax® credit inquiry based on the information they provided:"
              )}
            </Text>
          </Box>
          <Box borderRadius="small" border="borderNeutralLight" padding="small">
            <Stack space="gutter">
              <InfoItem
                title={t('l.full_legal_name.text', 'Full legal name')}
                value={data?.full_name}
                isLoading={isLoading}
              />
              <InfoItem
                title={t('l.date_of_birth.text', 'Date of birth')}
                value={formatedDate}
                isLoading={isLoading}
              />
              <InfoItem
                title={t('l.trust_score_auth.current_address.text', 'Current address')}
                tooltipText={t(
                  'l.trust_score_auth.current_address.tooltip',
                  'Verified by Canada Post'
                )}
                value={fullAddress}
                isLoading={isLoading}
              />
              <InfoItem
                title={t(
                  'l.trust_score_auth.identification_document.text',
                  'Identification document'
                )}
                errorText={t(
                  'l.trust_score_auth.identification_document.error',
                  "Without submitted identification documents, the Credit Summary section in this applicant's Trust Score report may lack accuracy in confirming a match with Equifax® records."
                )}
                tooltipText={t(
                  'l.trust_score_auth.identification_document.tooltip',
                  'Valid Social Insurance Number (SIN) or Driver’s License'
                )}
                showSuccess={data?.identification_provided}
                isLoading={isLoading}
              />
            </Stack>
          </Box>
          <Box>
            <Text size="xsmall" weight="regular" align="left" tone="tertiary">
              <em>
                {t(
                  'l.trust_score_auth.confirmation.text',
                  'By clicking “I confirm”, you acknowledge and agree to ensure the accuracy of this information. liv.rent bears no responsibility for any failed credit checks resulting from inaccuracies in the provided tenant details.'
                )}
              </em>
            </Text>
          </Box>
        </Stack>
        <Box
          width="full"
          display="flex"
          justifyContent="flexEnd"
          paddingTop={{ mobile: 'medium', desktop: 'large' }}
        >
          <Box marginBottom={{ mobile: 'small', desktop: 'none' }}>
            <Button variant="solid" onClick={onConfirmHandler}>
              {t('l.application.view.i_confirm.button', 'I Confirm')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  )
}

export default TrustScoreAgreementModal
